import apiClient from './client';

/** Agent 基础信息、Profile 及其子资源（人设/技能/工作流/记忆/工具/MCP）API 客户端。 */

/** Agent 基础信息（含 LLM 配置标记） */
export interface AgentBasic {
  id: string;
  name: string;
  description: string | null;
  active_profile_id: string | null;
  capability_id?: string | null;
  capability_version?: string | null;
  llm_config_id?: string | null;
  [key: string]: unknown;
}

/** 能力档案（Profile）：人设 / 形象 / 声音 / LLM / extra */
export interface AgentProfile {
  id: string;
  agent_id: string;
  version: string;
  status: string;
  persona_config: Record<string, unknown> | null;
  avatar_config: Record<string, unknown> | null;
  voice_config: Record<string, unknown> | null;
  llm_config_id: string | null;
  extra: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ProfileSkill {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  definition: Record<string, unknown>;
  source: string;
  priority: number;
  is_enabled: boolean;
  /** B17 信任档位：quarantined / active / global（后端追加的可选字段） */
  trust_state?: string;
}

export interface ProfileWorkflow {
  id: string;
  name: string;
  description: string | null;
  entry_state: string;
  states: Record<string, unknown>;
  exceptions: unknown[] | null;
  global_vars: Record<string, unknown> | null;
  is_enabled: boolean;
}

export interface ProfileMemory {
  id: string;
  memory_type: string;
  description: string | null;
  config: Record<string, unknown>;
}

export interface ProfileTool {
  id: string;
  tool_id: string;
  alias: string | null;
  override_config: Record<string, unknown> | null;
  is_enabled: boolean;
}

export interface GlobalTool {
  id: string;
  name: string;
  description?: string;
  schema?: Record<string, unknown>;
  implementation?: Record<string, unknown>;
  source?: string;
}

export interface PersonaTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  tags?: string[];
  config: Record<string, unknown>;
  is_builtin: boolean;
}

export interface KnowledgeItem {
  id: string;
  content: string;
  category?: string;
}

/** transport 三态：stdio（本地子进程）/ http / sse（远程 MCP server）。 */
export type McpServerTransport = 'stdio' | 'http' | 'sse';

export interface McpServerEntity {
  id: string;
  name: string;
  description?: string;
  /** 启动命令；远程条目为 null（后端已放宽为可空） */
  command?: string | null;
  args?: unknown[];
  env?: Record<string, string>;
  /** stdio / http / sse（缺省 stdio，兼容存量条目） */
  transport?: McpServerTransport;
  /** 远程条目 URL（http/sse） */
  url?: string | null;
  /** 是否已配置 Bearer token（后端不回传 token 值） */
  has_token?: boolean;
}

export interface McpServerUpsertPayload {
  name: string;
  description?: string;
  /** 启动命令（stdio 必填；表单未填时透传 undefined，JSON 序列化后字段缺省） */
  command?: string;
  args?: unknown[];
  env?: Record<string, string>;
  /** 远程 transport：http / sse（缺省 stdio，保持存量行为） */
  transport?: McpServerTransport;
  /** 远程条目 URL（http/sse 必填） */
  url?: string;
  /** 远程条目自定义请求头（明文；密钥请走 token） */
  headers?: Record<string, string>;
  /** Bearer token（write-only：只发不回；空串=清除） */
  token?: string;
}

export type SkillTrustTargetState = 'active' | 'global';

// ── Agent 基础 ──

export async function getAgent(agentId: string): Promise<AgentBasic> {
  const res = await apiClient.get(`/agents/${agentId}`);
  return res.data;
}

// ── Profile ──

export async function listAgentProfiles(agentId: string): Promise<AgentProfile[]> {
  const res = await apiClient.get(`/agents/${agentId}/profiles`);
  return res.data;
}

export async function getAgentProfile(profileId: string): Promise<AgentProfile> {
  const res = await apiClient.get(`/profiles/${profileId}`);
  return res.data;
}

export async function updateProfilePersona(profileId: string, personaConfig: object): Promise<void> {
  await apiClient.put(`/profiles/${profileId}`, { persona_config: personaConfig });
}

export async function updateProfileAvatar(profileId: string, data: object): Promise<void> {
  await apiClient.put(`/profiles/${profileId}/avatar`, data);
}

export async function updateProfileVoice(profileId: string, data: object): Promise<void> {
  await apiClient.put(`/profiles/${profileId}/voice`, data);
}

export async function activateProfile(profileId: string): Promise<void> {
  await apiClient.post(`/profiles/${profileId}/activate`);
}

export async function cloneProfile(sourceId: string, version: string): Promise<void> {
  await apiClient.post(`/profiles/${sourceId}/clone`, { version });
}

export async function applyPersonaTemplate(agentId: string, templateId: string): Promise<void> {
  await apiClient.post(`/agents/${agentId}/persona/apply-template/${templateId}`);
}

// ── Profile 子资源 ──

export async function listProfileSkills(profileId: string): Promise<ProfileSkill[]> {
  const res = await apiClient.get(`/profiles/${profileId}/skills`);
  return res.data;
}

export async function listProfileWorkflows(profileId: string): Promise<ProfileWorkflow[]> {
  const res = await apiClient.get(`/profiles/${profileId}/workflows`);
  return res.data;
}

export async function listProfileMemories(profileId: string): Promise<ProfileMemory[]> {
  const res = await apiClient.get(`/profiles/${profileId}/memories`);
  return res.data;
}

export async function listProfileTools(profileId: string): Promise<ProfileTool[]> {
  const res = await apiClient.get(`/profiles/${profileId}/tools`);
  return res.data;
}

export async function listGlobalTools(): Promise<GlobalTool[]> {
  const res = await apiClient.get('/tools');
  return res.data;
}

export async function listPersonaTemplates(): Promise<PersonaTemplate[]> {
  const res = await apiClient.get('/persona/templates');
  return res.data;
}

export async function listAgentKnowledge(agentId: string): Promise<KnowledgeItem[]> {
  const res = await apiClient.get(`/agents/${agentId}/knowledge`);
  return res.data;
}

export async function setProfileSkillEnabled(profileId: string, skillId: string, isEnabled: boolean): Promise<void> {
  await apiClient.put(`/profiles/${profileId}/skills/${skillId}`, { is_enabled: isEnabled });
}

/** B17：人工提升技能信任档位（后端按 OWNER/ADMIN 校验，越权 403） */
export async function setProfileSkillTrust(
  profileId: string,
  skillId: string,
  targetState: SkillTrustTargetState
): Promise<void> {
  await apiClient.post(`/profiles/${profileId}/skills/${skillId}/trust`, { target_state: targetState });
}

export async function setProfileWorkflowEnabled(
  profileId: string,
  workflowId: string,
  isEnabled: boolean
): Promise<void> {
  await apiClient.put(`/profiles/${profileId}/workflows/${workflowId}`, { is_enabled: isEnabled });
}

export async function deleteProfileMemory(profileId: string, memoryId: string): Promise<void> {
  await apiClient.delete(`/profiles/${profileId}/memories/${memoryId}`);
}

export async function attachProfileTool(profileId: string, toolId: string): Promise<void> {
  await apiClient.post(`/profiles/${profileId}/tools`, { tool_id: toolId, is_enabled: true });
}

export async function detachProfileTool(profileId: string, toolRowId: string): Promise<void> {
  await apiClient.delete(`/profiles/${profileId}/tools/${toolRowId}`);
}

/** 「说人话搜索」：本地 Skill 路由器按相关度排序，返回命中的技能 ID 列表。 */
export async function routeSkillSearch(agentId: string, term: string): Promise<string[]> {
  const res = await apiClient.get(`/agents/${agentId}/skills/route`, { params: { query: term } });
  return (res.data as Array<{ skill_id: string }>).map((hit) => hit.skill_id);
}

// ── MCP Server 一等实体（写操作后端同步回 active profile 的 extra.mcp_servers） ──

export async function listAgentMcpServers(agentId: string): Promise<McpServerEntity[]> {
  const res = await apiClient.get(`/agents/${agentId}/mcp-servers`);
  return res.data || [];
}

export async function createAgentMcpServer(agentId: string, payload: McpServerUpsertPayload): Promise<void> {
  await apiClient.post(`/agents/${agentId}/mcp-servers`, {
    name: payload.name,
    description: payload.description,
    ...(payload.transport && payload.transport !== 'stdio' ? { transport: payload.transport } : {}),
    ...(payload.command !== undefined && payload.command !== null ? { command: payload.command } : {}),
    args: payload.args || [],
    env: payload.env || {},
    ...(payload.url !== undefined ? { url: payload.url } : {}),
    ...(payload.headers !== undefined ? { headers: payload.headers } : {}),
    ...(payload.token !== undefined ? { token: payload.token } : {}),
  });
}

export async function updateAgentMcpServer(
  agentId: string,
  serverId: string,
  payload: McpServerUpsertPayload
): Promise<void> {
  await apiClient.put(`/agents/${agentId}/mcp-servers/${serverId}`, {
    name: payload.name,
    description: payload.description,
    ...(payload.transport && payload.transport !== 'stdio' ? { transport: payload.transport } : {}),
    ...(payload.command !== undefined && payload.command !== null ? { command: payload.command } : {}),
    args: payload.args || [],
    env: payload.env || {},
    ...(payload.url !== undefined ? { url: payload.url } : {}),
    ...(payload.headers !== undefined ? { headers: payload.headers } : {}),
    ...(payload.token !== undefined ? { token: payload.token } : {}),
  });
}

export async function deleteAgentMcpServer(agentId: string, serverId: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}/mcp-servers/${serverId}`);
}

/** MCP Server 连通性测试结果（后端三态：passed / failed / degraded）。 */
export interface McpServerTestResult {
  /** passed=握手+tools/list 通过；failed=可证伪不合规；degraded=环境不可验证（≠不可用） */
  status: 'passed' | 'failed' | 'degraded';
  tool_count: number;
  tools: Array<{ name: string; description: string }>;
  duration_ms: number;
  detail: string;
  static_issues: string[];
  suggestions: string[];
}

/** 运行态连通性测试：静态检查 + 真实 stdio 拉起（initialize + tools/list）。 */
export async function testAgentMcpServer(agentId: string, serverId: string): Promise<McpServerTestResult> {
  const res = await apiClient.post(`/agents/${agentId}/mcp-servers/${serverId}/test`);
  return res.data;
}
